/**
 * Whether an edit to a composer's draft corrected a dictated word, so the
 * composer can offer to remember it for voice (Decision 14). Tokens split on
 * whitespace; they compare by a minimal core (letters and digits, lowercase),
 * so case-only and punctuation-only edits are never corrections. Rust owns
 * the real normalisation when the correction is learned.
 */

/** The draft right after a final transcript, and the transcript's token
 *  range in it: `dictatedFrom` inclusive, `dictatedTo` exclusive. */
export type DictatedSnapshot = {
  text: string;
  dictatedFrom: number;
  dictatedTo: number;
};

/** The replaced snapshot tokens (`from` inclusive, `to` exclusive) and the
 *  word that replaced them. */
export type DictatedSubstitution = { from: number; to: number; word: string };

const MAX_TOKENS = 3;
const NOT_WORD = /[^\p{L}\p{N}]+/gu;
/** Sentence punctuation, quotes and brackets around the learned word; what
 *  belongs to the word itself ("C++", ".NET") stays. */
const AROUND_WORD = /^["'“”‘’«»([{¿¡]+|["'“”‘’«»)\]}.,!?;:…]+$/gu;

export const voiceTokens = (text: string) => text.split(/\s+/).filter(Boolean);

const core = (token: string) => token.replace(NOT_WORD, "").toLowerCase();

export function dictatedSubstitution(
  snapshot: DictatedSnapshot,
  value: string,
): DictatedSubstitution | null {
  const before = voiceTokens(snapshot.text).map(core);
  const afterTokens = voiceTokens(value);
  const after = afterTokens.map(core);
  let start = 0;
  while (
    start < before.length &&
    start < after.length &&
    before[start] === after[start]
  ) {
    start += 1;
  }
  let end = 0;
  while (
    end < before.length - start &&
    end < after.length - start &&
    before[before.length - 1 - end] === after[after.length - 1 - end]
  ) {
    end += 1;
  }
  const to = before.length - end;
  const changedTo = after.length - end;
  const heardCount = to - start;
  const wordCount = changedTo - start;
  if (heardCount < 1 || heardCount > MAX_TOKENS) return null;
  if (wordCount < 1 || wordCount > MAX_TOKENS) return null;
  if (start < snapshot.dictatedFrom || to > snapshot.dictatedTo) return null;
  const word = afterTokens
    .slice(start, changedTo)
    .join(" ")
    .replace(AROUND_WORD, "");
  const heardCore = before.slice(start, to).join("");
  if (!word || core(word) === heardCore) return null;
  return { from: start, to, word };
}
